'use client';

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { cn } from '@/lib/utils';

export type ScrollFadeSlotProps = {
  children: ReactNode;
  /**
   * Ref to the scroll container that acts as the IntersectionObserver root.
   * The slot's opacity and edge mask are driven by how much of the slot is
   * inside this container's viewport (its `intersectionRatio`) — so the fade
   * tracks scroll position within the container, not within the page.
   */
  root: RefObject<HTMLElement | null>;
  /** Extra classes applied to the slot wrapper (sizing, layout, etc.). */
  className?: string;
};

/**
 * Wraps its children in a div that dims and softly masks its edges based on
 * how much of it is intersecting a scroll container — mirrors the SwiftUI
 * `ScrollTransition(.interactive)` identity phase. A slot fully inside the
 * container is untouched (opacity 1, no edge mask); a slot moving out of view
 * fades to a dim baseline and grows a subtle horizontal edge mask.
 *
 * The component is layout-agnostic: it renders a `<div>` and forwards any
 * `className` you pass. Callers own sizing, flex/grid placement, and scroll
 * snap. Pass the scroll container's ref as `root`.
 *
 * Honors `prefers-reduced-motion`: reduced-motion viewers see a solid render
 * with no fade or mask transitions.
 */
export function ScrollFadeSlot({
  children,
  root,
  className,
}: ScrollFadeSlotProps) {
  const ref = useRef<HTMLDivElement>(null);
  // SSR and first client render use the dim baseline. Slots that turn out to
  // be in the identity phase animate up to opacity 1 (and shed the mask) as
  // soon as the observer fires its initial callback on mount — no flash of
  // invisible content and no hydration mismatch.
  const [state, setState] = useState<SlotState>(INITIAL_STATE);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    // Reduced-motion viewers bypass both effects: threshold 0 guarantees
    // IntersectionObserver's initial sync callback fires for every observed
    // element regardless of whether it's in view, and the callback
    // short-circuits to a fully opaque state. Doing the reveal from a
    // callback (not synchronously in the effect body) keeps the
    // `react-hooks/set-state-in-effect` lint rule satisfied.
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (prefersReduced) {
            setState({ opacity: 1, mask: buildMask('0%') });
            return;
          }
          const ratio = entry.intersectionRatio;
          setState({
            opacity: opacityForRatio(ratio),
            mask: buildMask(maskEdgeStopForRatio(ratio)),
          });
        });
      },
      { root: root.current, threshold: prefersReduced ? 0 : THRESHOLDS },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [root]);

  return (
    <div
      ref={ref}
      // The 150ms opacity transition interpolates between the discrete opacity
      // values IO publishes as thresholds are crossed, so scrolling reads as
      // continuous rather than stepped. The mask can't be interpolated in CSS
      // (linear-gradient stops aren't animatable) but the many IO thresholds
      // already produce ~40 discrete updates per full scroll, so it still
      // reads as smooth in practice.
      className={cn(
        'transition-opacity duration-150 ease-out motion-reduce:transition-none',
        className,
      )}
      style={{
        opacity: state.opacity,
        WebkitMaskImage: state.mask,
        maskImage: state.mask,
      }}
    >
      {children}
    </div>
  );
}

// ---------- ratio → visual math ----------

/** Opacity floor for a slot that's fully outside the scroll container. */
const MIN_OPACITY = 0.2;
/**
 * Once a slot is at least this fraction inside the container, it's considered
 * in the identity phase and renders at full opacity with no mask. Kept below
 * 1 so full brightness arrives before the slot is literally 100% in view —
 * otherwise a slot wider than the container could never reach opacity 1.
 */
const IDENTITY_RATIO = 0.85;
/**
 * Enough thresholds that IntersectionObserver fires on nearly every scroll
 * frame; the CSS transition on opacity smooths any residual steps.
 */
const THRESHOLDS = Array.from({ length: 41 }, (_, i) => i / 40);
/**
 * Max fraction of the slot width covered by the mask fade at each edge. Kept
 * small so identity slots read as crisp and the effect stays subtle even for
 * a fully out-of-view slot.
 */
const MASK_FADE_MAX = 0.18;
/**
 * Alpha of the mask's outermost stop. Full transparency (0) would make the
 * slot's edge disappear entirely; keeping some alpha dims the edge without
 * erasing it, which is the "subtle blur into the background" feel the design
 * calls for.
 */
const MASK_EDGE_ALPHA = 0.55;

/**
 * Cubic ease-in: opacity hugs `MIN_OPACITY` while the slot is peeking in and
 * only ramps up in the second half of the transition, matching the "dim until
 * nearly centered" feel of a SwiftUI `ScrollTransition(.interactive)`. Linear
 * interpolation surfaced peeking slots at ~0.5 opacity, cubic keeps a
 * ~46%-intersecting slot around 0.33.
 */
const opacityForRatio = (ratio: number): number => {
  if (ratio >= IDENTITY_RATIO) {
    return 1;
  }
  const t = ratio / IDENTITY_RATIO;
  return MIN_OPACITY + (1 - MIN_OPACITY) * t * t * t;
};

/**
 * How wide the mask's edge fade should be at the given intersection ratio.
 * Returns a percentage string ready to paste into `linear-gradient()` stops.
 * Identity slots resolve to `0%` — the mask stays fully opaque so the slot
 * renders with crisp edges. Slots moving out of view fade their edges more
 * and more, tracking the same cubic ease as the opacity.
 */
const maskEdgeStopForRatio = (ratio: number): string => {
  if (ratio >= IDENTITY_RATIO) {
    return '0%';
  }
  const t = ratio / IDENTITY_RATIO;
  const fade = 1 - t * t * t;
  return `${(fade * MASK_FADE_MAX * 100).toFixed(2)}%`;
};

const buildMask = (edgeStop: string): string => {
  // Identity slots want a fully opaque mask — return a solid black gradient
  // so no interpolation between "transparent" and "black" softens the edges.
  if (edgeStop === '0%') {
    return 'linear-gradient(to right, black, black)';
  }
  return (
    `linear-gradient(to right, ` +
    `rgba(0, 0, 0, ${MASK_EDGE_ALPHA}) 0%, ` +
    `black ${edgeStop}, ` +
    `black calc(100% - ${edgeStop}), ` +
    `rgba(0, 0, 0, ${MASK_EDGE_ALPHA}) 100%)`
  );
};

type SlotState = { opacity: number; mask: string };

const INITIAL_STATE: SlotState = {
  opacity: MIN_OPACITY,
  // Start with a soft edge mask so SSR-rendered slots (which don't know their
  // intersection ratio yet) look consistent with off-screen slots after
  // hydration. The observer's initial callback replaces this on mount.
  mask: buildMask(`${MASK_FADE_MAX * 100}%`),
};
